import { enTransicionEstudiante, cuotaPara, caraMipaePlus, CUOTA_EVOLUCION_SEMANA, CUOTA_EVOLUCION_SEMANA_PLUS, CUOTA_PLUS } from "../lib/plan";
import { PRODUCTOS_PLUS, productoCubreModo } from "../lib/productosPlus";

// Comparación compacta Gratis vs la membresía de la experiencia, para Planes
// y el paywall.
//
// Desde el 17-oct se venden tres membresías y la tabla compara contra la de
// la experiencia de la persona (Estudiante Plus o Enfermero Plus), con las
// MISMAS palabras y números de la tabla de /precios: el límite razonable de
// la membresía con su número (CUOTA_PLUS, CUOTA_EVOLUCION_SEMANA_PLUS) y
// "Sin cuota mensual" para el PAE, jamás "sin tope", porque las funciones
// con IA tienen límites de uso en todos los planes (Términos §6). Solo van las filas que cambian con
// esa membresía: el PAE y los ramos son de MiPAE Estudiante, la evolución
// completa con IA de MiPAE Enfermero.
//
// Lista SOLO lo que de verdad separa a los planes (decisión del fundador
// 2026-09-14, ops/planes/version-extendida.md): topes de IA vs sin tope,
// Haiku vs Sonnet en Benito, memoria de ramos, mis procedimientos, PDF de
// Benito y PAE con tu pauta. Ningún módulo es de pago: MiPAE en turno, la
// evolución, la entrega y el PAE Detallado son gratis y por eso NO aparecen
// acá (la tabla vieja los vendía como candado; no restaurarla).
//
// Las cifras salen de la cuota real del rol (cuotaPara), así nunca prometemos
// de más. No es un contador "te quedan N": es la cuota del plan, la misma que
// declaran los Términos.
const Check = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function Celda({ v, plus }) {
  if (v === true) return <span className={plus ? "text-plus" : "text-clinical-stable"}><Check /></span>;
  if (v === null || v === false) return <span className="text-ink-faint">·</span>;
  return <span className={`text-12px font-sans ${plus ? "text-plus font-semibold" : "text-ink-muted"}`}>{v}</span>;
}

// La membresía contra la que se compara: la de la experiencia, o Estudiante
// Plus si la cara no vende (sin rol).
export function productoComparado(profile) {
  return caraMipaePlus(profile).producto || 'plus_estudiante';
}

// Exportada para test: la lista es el contrato con los Términos §6 y /precios.
export function filasComparacion(profile, producto = productoComparado(profile)) {
  const cuota = cuotaPara(profile);
  const estudiante = profile?.rol === 'estudiante' && !enTransicionEstudiante(profile);
  const conEstudiante = productoCubreModo(producto, 'estudiante');
  const conEnfermero = productoCubreModo(producto, 'enfermero');
  return [
    // "Consultas" y no "llamadas": el tope cuenta llamadas de ai_calls (un
    // mensaje puede valer más de una), pero nadie afuera sabe qué es una
    // llamada. Misma palabra en Términos §6 y en el correo del 21-sep.
    { k: "Benito, consultas al día", free: `Hasta ${cuota.benitoDia}`, plus: `Hasta ${CUOTA_PLUS.benitoDia}` },
    { k: "Benito, consultas al mes", free: "Rige el límite diario", plus: `Hasta ${CUOTA_PLUS.benitoMes}, compartidas por cuenta` },
    { k: "Benito, calidad de respuesta", free: "Su mejor modelo solo en el PAE", plus: "Su mejor modelo en todo" },
    // La cuota `paes` cuenta la capa diagnosticos, que emiten AMBOS PAE; el
    // Detallado además descuenta de `detallados`. Se dice tal cual.
    ...(conEstudiante ? [
      { k: "PAE Rápido o Detallado", free: estudiante ? '1/mes, compartido con tu pauta' : `${cuota.paes}/mes en total`, plus: "Sin cuota mensual" },
      { k: "De ellos, PAE Detallado", free: estudiante ? 'Dentro del mismo cupo' : `hasta ${cuota.detallados}/mes`, plus: "Sin cuota mensual" },
      { k: "PAE con tu pauta", free: estudiante ? 'Dentro del mismo cupo' : `${cuota.pautas}/mes`, plus: "Sin cuota mensual" },
    ] : []),
    // La evolución es una CUOTA desde el 25-oct (1 por semana en el plan
    // gratis), no un módulo de pago: MiPAE en turno sigue en los dos planes.
    ...(conEnfermero ? [
      { k: "Evolución completa con IA", free: `${CUOTA_EVOLUCION_SEMANA} por semana`, plus: `Hasta ${CUOTA_EVOLUCION_SEMANA_PLUS} por semana` },
    ] : []),
    ...(conEstudiante ? [{ k: "Memoria de tus ramos en Benito", free: null, plus: true }] : []),
    { k: "Fármacos y escalas", free: "Selección gratuita", plus: "Catálogo completo" },
    { k: "Mis procedimientos", free: "3 de prueba", plus: "Hasta 20" },
    { k: "Respuesta de Benito en PDF", free: null, plus: true },
  ];
}

export default function ComparaPlanes({ profile, className = "" }) {
  const producto = productoComparado(profile);
  const filas = filasComparacion(profile, producto);
  const conEnfermero = productoCubreModo(producto, 'enfermero');
  const cols = "grid grid-cols-[1fr_6.5rem_5.5rem] items-center";
  return (
    <div className={`rounded-xl border border-edge overflow-hidden ${className}`}>
      <div className={`${cols} bg-surface/60 px-3 py-2`}>
        <span />
        <span className="text-10px font-semibold uppercase tracking-[0.06em] text-ink-muted font-sans text-center">Gratis</span>
        <span className="text-10px font-semibold uppercase tracking-[0.06em] text-plus font-sans text-center">{PRODUCTOS_PLUS[producto].nombre}</span>
      </div>
      {filas.map((f) => (
        <div key={f.k} className={`${cols} px-3 py-2 border-t border-edge/70`}>
          <span className="text-12.5px text-ink font-sans">{f.k}</span>
          <span className="flex justify-center text-center"><Celda v={f.free} /></span>
          <span className="flex justify-center text-center"><Celda v={f.plus} plus /></span>
        </div>
      ))}
      <p className="px-3 py-2 border-t border-edge/70 text-10px text-ink-muted font-sans leading-snug">
        {enTransicionEstudiante(profile) && "Tu cuenta conserva estos límites hasta el 24 de octubre. Desde el 25: 1 PAE mensual compartido y 3 consultas diarias a Benito. "}
        La guía y el registro de MiPAE en turno, la entrega, el laboratorio con sus rangos, las calculadoras y la Bitácora son gratis en los dos planes. {conEnfermero ? "La evolución con IA se renueva cada lunes" : "El cupo del PAE se renueva el 1 de cada mes"}; el cupo diario de Benito, a medianoche, y el mensual, el día 1, hora de Chile. Los cupos de Benito son por cuenta y no se acumulan.
      </p>
    </div>
  );
}

